import { convertTemperature } from './temperature-utils';
import { getPlural } from './data/unit-plurals';

type ConversionMap = {
  [key: string]: {
    [key: string]: number;
  };
};

const conversionFactors: ConversionMap = {
  length: {
    inch: 1,
    foot: 12,
    yard: 36,
    mile: 63360,
    league: 190080,
    rod: 198,
    furlong: 7920,
    "roman foot": 11.65,
    cubit: 18,
    hand: 4,
    span: 9,
    palm: 3,
    meter: 39.3701,
    centimeter: 0.393701,
    kilometer: 39370.1,
    "nautical mile": 72913.4,
    fathom: 72,
    cable: 7200,
    ell: 45,
    chain: 792,
    morgen: 79200,
    hide: 475200
  },
  weight: {
    pound: 1,
    ounce: 0.0625,
    stone: 14,
    grain: 0.000142857,
    dram: 0.00390625,
    hundredweight: 112,
    kilogram: 2.20462,
    ton: 2204.62,
    gram: 0.00220462,
    carat: 0.000440925,
    talent: 75,
    shekel: 0.0625
  },
  volume: {
    gallon: 1,
    quart: 0.25,
    pint: 0.125,
    "fluid ounce": 0.0078125,
    gill: 0.03125,
    bushel: 8,
    liter: 0.264172,
    "cubic meter": 264.172,
    hogshead: 63,
    firkin: 9,
    butt: 126,
    peck: 2,
    pottle: 0.5
  },
  time: {
    second: 1,
    minute: 60,
    hour: 3600,
    day: 86400,
    week: 604800,
    month: 2592000,
    year: 31536000,
    fortnight: 1209600,
    moment: 90,
    jiffy: 0.01
  },
  area: {
    "square meter": 1,
    acre: 4046.86,
    hectare: 10000,
    "square kilometer": 1000000,
    "square mile": 2589988.11,
    barn: 1e-28,
    morgen: 2500,
    hide: 486000
  },
  speed: {
    "meters per second": 1,
    "miles per hour": 0.44704,
    "kilometers per hour": 0.277778,
    knot: 0.514444,
    "furlong per fortnight": 0.00016630952380952382
  }
};

export function convertValue(value: number, fromUnit: string, toUnit: string): number {
  if (isTemperatureUnit(fromUnit) && isTemperatureUnit(toUnit)) {
    return convertTemperature(value, fromUnit as any, toUnit as any);
  }

  const measurementType = Object.entries(conversionFactors).find(([_, units]) => 
    fromUnit in units && toUnit in units
  )?.[0];

  if (!measurementType) {
    throw new Error("Units must be of the same measurement type");
  }

  const fromFactor = conversionFactors[measurementType][fromUnit];
  const toFactor = conversionFactors[measurementType][toUnit];

  const baseValue = value * fromFactor;
  return baseValue / toFactor;
}

function isTemperatureUnit(unit: string): boolean {
  return ['celsius', 'fahrenheit', 'kelvin', 'reaumur', 'rankine'].includes(unit.toLowerCase());
}

export function formatConversionResult(value: number, fromUnit: string, toUnit: string): string {
  const result = convertValue(value, fromUnit, toUnit);
  return `${value} ${value !== 1 ? getPlural(fromUnit) : fromUnit} = ${result.toFixed(2)} ${result !== 1 ? getPlural(toUnit) : toUnit}`;
}

export function validateConversion(value: string, fromUnit: string, toUnit: string): string | null {
  if (!value || isNaN(Number(value))) {
    return "Please enter a valid number";
  }

  if (!fromUnit || !toUnit) {
    return "Please select both units for conversion";
  }

  const numValue = Number(value);
  if (numValue < 0 && !isTemperatureUnit(fromUnit)) {
    return "Please enter a positive number";
  }

  if (isTemperatureUnit(fromUnit) !== isTemperatureUnit(toUnit)) {
    return "Units must be of the same measurement type";
  }

  return null;
}
